import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Users, FileText, Settings, BarChart3, CreditCard, Share2, Gift, Bell, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { ThemeContext } from '../context/ThemeContext';
import Navbar from './Navbar';
import Footer from './Footer';
import ReviewInvitationPopup from './ReviewInvitationPopup';
import { checkReviewEligibility, getFeaturedReviews } from '../services/reviews';
import { isAuthenticated } from '../services/auth';

const HomePage = () => {
  const { darkMode } = useContext(ThemeContext);
  const [currentSlide, setCurrentSlide] = useState(2);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [hasNotified, setHasNotified] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  // Check if user just registered and needs to see verification modal
  useEffect(() => {
    const shouldShowModal = sessionStorage.getItem('showVerificationModal');
    if (shouldShowModal === 'true') {
      setShowVerificationModal(true);
      sessionStorage.removeItem('showVerificationModal');
    }
  }, []);

  // Check if user is eligible for review popup
  useEffect(() => {
    const checkReviewPopup = async () => {
      if (isAuthenticated()) {
        try {
          const eligibility = await checkReviewEligibility();
          if (eligibility.showPopup) {
            setShowReviewPopup(true);
          }
        } catch (err) {
          console.error('Error checking review popup:', err);
        }
      }
    };
    checkReviewPopup();
  }, []);

  // Color palette for testimonial avatars
  const avatarColors = [
    "bg-blue-600", "bg-green-600", "bg-purple-600", "bg-red-600",
    "bg-yellow-600", "bg-indigo-600", "bg-pink-600", "bg-teal-600",
    "bg-orange-600", "bg-cyan-600"
  ];

  // Placeholder testimonials (fallback if no reviews in database)
  const placeholderTestimonials = [
    {
      initials: "JM",
      name: "John Martinez",
      company: "Martinez Auto Repair",
      text: "Torque has completely transformed how we handle invoicing and customer management. The time we save on paperwork can now be spent on actual repairs.",
      rating: 5,
      color: "bg-blue-600"
    },
    {
      initials: "SW",
      name: "Sarah Wilson",
      company: "Wilson Automotive",
      text: "The vehicle database feature is incredible. Being able to quickly look up specifications and service history has improved our diagnostic accuracy and efficiency.",
      rating: 5,
      color: "bg-green-600"
    },
    {
      initials: "MJ",
      name: "Michael Johnson",
      company: "Johnson's Garage",
      text: "Best investment we've made in years! The invoice customization options make our shop look incredibly professional. Customer feedback has been amazing.",
      rating: 5,
      color: "bg-red-600"
    },
    {
      initials: "LC",
      name: "Linda Chen",
      company: "Chen Auto Service",
      text: "The customer database keeps everything organized. We can pull up any customer's complete history in seconds. No more digging through file cabinets!",
      rating: 5,
      color: "bg-yellow-600"
    },
    {
      initials: "DT",
      name: "David Thompson",
      company: "Thompson's Transmission",
      text: "Training my staff on Torque was surprisingly easy. The interface is intuitive and the support team was there every step of the way.",
      rating: 5,
      color: "bg-indigo-600"
    },
    {
      initials: "KP",
      name: "Karen Patterson",
      company: "Patterson Auto Works",
      text: "The payment tracking feature has helped us reduce outstanding invoices by 40%. We can see exactly who owes what and send automated reminders.",
      rating: 5,
      color: "bg-pink-600"
    },
    {
      initials: "TA",
      name: "Tom Anderson",
      company: "Anderson's Auto Body",
      text: "We've been using Torque for over a year now and couldn't be happier. The regular updates and new features show they really listen to their customers.",
      rating: 5,
      color: "bg-teal-600"
    },
    {
      initials: "NR",
      name: "Nicole Rodriguez",
      company: "Rodriguez Motors",
      text: "The analytics dashboard gives me real-time visibility into my business performance. I can make informed decisions on the fly instead of waiting for month-end reports.",
      rating: 5,
      color: "bg-orange-600"
    },
    {
      initials: "BW",
      name: "Brian White",
      company: "White's Automotive Center",
      text: "Switching to Torque was the easiest transition we've ever done. They imported all our existing data and had us up and running in less than a day.",
      rating: 5,
      color: "bg-cyan-600"
    },
    {
      initials: "RG",
      name: "Rachel Green",
      company: "Green's Auto Care",
      text: "Finally, software that understands what auto shops actually need. The workflow is perfectly designed for how we operate day-to-day.",
      rating: 5,
      color: "bg-purple-600"
    }
  ];

  // Fetch featured reviews from the database
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const reviews = await getFeaturedReviews(12);
        if (reviews && reviews.length > 0) {
          // Map API response to testimonial format
          const mappedTestimonials = reviews.map((review, index) => {
            const firstName = review.authorFirstName || 'Anonymous';
            const lastName = review.authorLastName || '';
            const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

            return {
              initials,
              name: `${firstName} ${lastName}`.trim(),
              company: review.authorCompanyName || '',
              text: review.content || '',
              rating: review.rating || 5,
              color: avatarColors[index % avatarColors.length]
            };
          });
          setTestimonials(mappedTestimonials);
        } else {
          // Use placeholder testimonials if no reviews in database
          setTestimonials(placeholderTestimonials);
        }
      } catch (err) {
        console.error('Error fetching testimonials:', err);
        // Fall back to placeholder testimonials on error
        setTestimonials(placeholderTestimonials);
      } finally {
        setTestimonialsLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  const slides = [
    {
      image: `${process.env.PUBLIC_URL}/images/torque2.png`,
      title: "Invoice Management Dashboard",
      description: "Create professional invoices, track payments, and manage your entire billing process with our intuitive dashboard"
    },
    {
      image: `${process.env.PUBLIC_URL}/images/torque2.png`,
      title: "Customer Database View",
      description: "Maintain comprehensive customer records including contact information, vehicle history, and service preferences"
    },
    {
      image: `${process.env.PUBLIC_URL}/images/torque2.png`,
      title: "Vehicle Service History",
      description: "Access detailed service records, maintenance schedules, and vehicle specifications with our integrated database"
    },
    {
      image: `${process.env.PUBLIC_URL}/images/torque2.png`,
      title: "Reports & Analytics",
      description: "Generate insightful reports on revenue, service trends, and customer patterns to make data-driven decisions"
    },
    {
      image: `${process.env.PUBLIC_URL}/images/torque2.png`,
      title: "Payment Processing",
      description: "Securely process payments with multiple payment methods and automatic payment tracking integration"
    }
  ];

  const handleImageClick = (slide) => {
    setSelectedImage(slide);
    setModalOpen(true);
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you for your message! In a real application, this would be sent to our team.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  // Auto-scroll testimonials (scroll by 1 at a time, show multiple based on screen size)
  const [testimonialsToShow, setTestimonialsToShow] = useState(3);

  useEffect(() => {
    const updateTestimonialsToShow = () => {
      const width = window.innerWidth;
      if (width >= 1280) setTestimonialsToShow(3); // xl screens
      else if (width >= 768) setTestimonialsToShow(2); // md screens
      else setTestimonialsToShow(1); // small screens
    };

    updateTestimonialsToShow();
    window.addEventListener('resize', updateTestimonialsToShow);
    return () => window.removeEventListener('resize', updateTestimonialsToShow);
  }, []);

  const maxTestimonialIndex = testimonials.length - testimonialsToShow;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => {
        if (prev >= maxTestimonialIndex) return 0;
        return prev + 1;
      });
    }, 5000); // Change testimonial every 5 seconds
    return () => clearInterval(interval);
  }, [maxTestimonialIndex]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => {
      if (prev >= maxTestimonialIndex) return 0;
      return prev + 1;
    });
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => {
      if (prev <= 0) return maxTestimonialIndex;
      return prev - 1;
    });
  };
  
  return (
    <div className={`min-h-screen flex flex-col ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900`}>
      <Navbar />

      {/* Main content */}
      <main className="flex-grow pt-16">
        {/* Hero Section */}
        <section id="home" className="bg-gradient-to-r from-blue-600 to-indigo-700 dark:from-blue-800 dark:to-indigo-900 py-16 lg:py-20 text-white">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-5 lg:px-7">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-12 text-center leading-tight">
              Streamline Your{' '}
              <span className="text-yellow-300">Automotive Business</span>{' '}
              with Torque
            </h1>
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="lg:w-1/3 text-center lg:text-left">
                <p className="text-xl md:text-2xl mb-8 text-blue-100 leading-relaxed">
                  The complete invoice management solution designed specifically for auto repair shops and service centers.
                </p>
                <Link 
                  to="/register" 
                  className="inline-flex items-center bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                >
                  <span>Start Free Trial</span>
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
              <div className="lg:w-2/3 relative">
                <div className="relative">
                  <div className="absolute inset-0 bg-blue-500 dark:bg-blue-600 blur-3xl opacity-20 rounded-full transform scale-110"></div>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/torque1.png`}
                    alt="Torque Software Dashboard"
                    className="relative rounded-xl w-full drop-shadow-2xl transform hover:scale-105 transition-transform duration-300"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 bg-white dark:bg-gray-800">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-5 lg:px-7">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Powerful Features for Your Automotive Business
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Designed with auto repair shops and service centers in mind, Torque Professional provides all the tools you need to manage your business efficiently.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Feature 1 */}
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Invoice Management</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Create, customize, and print professional invoices. Track payments and manage your entire billing process in one place.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="bg-green-100 dark:bg-green-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Customer Database</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Maintain detailed customer records including vehicle history, contact information, and previous service records.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="bg-purple-100 dark:bg-purple-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Camera className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Vehicle Management</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Access a comprehensive vehicle database with detailed specifications and service history for better diagnostics. Includes <strong>FREE yearly updates</strong>.
                </p>
              </div>

              {/* Feature 4 */}
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="bg-orange-100 dark:bg-orange-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Reports & Analytics</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Generate detailed reports on sales, service history, and customer trends to make data-driven business decisions.
                </p>
              </div>

              {/* Feature 5 */}
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="bg-red-100 dark:bg-red-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <CreditCard className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Payment Processing</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Securely process payments with integrated payment solutions, supporting multiple payment methods.
                </p>
              </div>

              {/* Feature 6 */}
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300">
                <div className="bg-teal-100 dark:bg-teal-900 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <Settings className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Customizable Settings</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Tailor the software to your business needs with customizable tax rates, company information, and invoice templates.
                </p>
              </div>
            </div>


            <div className="mt-16">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
                See Torque in Action
              </h3>
              <div className="max-w-[1440px] mx-auto">
                <div className="relative h-[500px] overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center" style={{ perspective: '1000px', transform: 'translateZ(0)' }}>
                    {slides.map((slide, index) => {
                      const offset = index - currentSlide;
                      const totalSlides = slides.length;
                      
                      // Normalize offset to handle wrapping
                      let normalizedOffset = offset;
                      if (offset > totalSlides / 2) normalizedOffset -= totalSlides;
                      if (offset < -totalSlides / 2) normalizedOffset += totalSlides;
                      
                      // V-shape arrangement with gap between main and side images
                      const absOffset = Math.abs(normalizedOffset);
                      const spacing = absOffset === 0 ? 0 : 40; // Add gap between main and side images
                      const x = normalizedOffset === 0 ? 0 : (normalizedOffset * 220) + (Math.sign(normalizedOffset) * spacing);
                      const z = -absOffset * 120; // Move back based on distance from center
                      
                      // Scale based on position
                      const scale = normalizedOffset === 0 ? 1.2 : 1 - (absOffset * 0.2);
                      
                      const isActive = normalizedOffset === 0;
                      const isVisible = absOffset <= 2; // Only show 5 images at a time
                      
                      // Progressive darkening: 1.0 for center, down to 0.3 for farthest
                      let opacity = isActive ? 1 : Math.max(0.3, 0.8 - (absOffset * 0.25));
                      let zIndex = 5 - absOffset;
                      
                      // Image dimensions
                      const baseWidth = 360; // Reduced from 400 to make skinnier
                      const baseHeight = 300;
                      const width = baseWidth * scale;
                      const height = baseHeight * scale;
                      
                      if (!isVisible) return null;
                      
                      return (
                        <div
                          key={index}
                          className={`absolute transition-all duration-700 ease-out flex items-center justify-center`}
                          style={{
                            transform: `translateX(${x}px) translateZ(${z}px)`,
                            zIndex,
                            opacity,
                            transformStyle: 'preserve-3d',
                            width: 'auto',
                            height: 'auto'
                          }}
                        >
                          <img
                            src={slide.image}
                            alt={`Torque Software Demo ${index + 1}`}
                            className="rounded-lg drop-shadow-2xl cursor-pointer transition-transform hover:scale-105"
                            style={{
                              width: 'auto',
                              height: `${height}px`,
                              maxWidth: `${width}px`,
                              filter: isActive ? 'brightness(1)' : `brightness(${Math.max(0.4, 1 - (absOffset * 0.2))})`
                            }}
                            onClick={() => isActive ? handleImageClick(slide) : setCurrentSlide(index)}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1))}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors z-20"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1))}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors z-20"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </div>
                <div className="mt-8 text-center px-4">
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white transition-all duration-500 mb-2">
                    {slides[currentSlide].title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-lg transition-all duration-500">
                    {slides[currentSlide].description}
                  </p>
                </div>
              </div>
              
              {/* Image Modal */}
              {modalOpen && selectedImage && (
                <div 
                  className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                  onClick={() => setModalOpen(false)}
                >
                  <div className="relative max-w-5xl w-full h-full flex items-center justify-center">
                    <button
                      onClick={() => setModalOpen(false)}
                      className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 p-2 rounded-full z-10"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <img
                      src={selectedImage.image}
                      alt={selectedImage.description}
                      className="max-w-full max-h-full object-contain rounded-lg"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="bg-gray-100 dark:bg-gray-900 py-20">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-5 lg:px-7">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Trusted by Auto Repair Professionals
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Hear what our customers have to say about how Torque Professional has transformed their business operations.
              </p>
            </div>

            <div className="relative max-w-7xl mx-auto">
              <div className="flex items-center gap-4">
                {/* Left Navigation Arrow */}
                <button
                  onClick={prevTestimonial}
                  className="flex-shrink-0 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-white p-3 rounded-full shadow-lg transition-colors z-10"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>

                {/* Testimonial Carousel Container */}
                <div className="flex-grow overflow-hidden">
                  <div
                    className="flex transition-transform duration-700 ease-in-out"
                    style={{
                      transform: `translateX(-${currentTestimonial * (100 / testimonialsToShow)}%)`,
                    }}
                  >
                    {testimonials.map((testimonial, index) => (
                      <div
                        key={index}
                        className="flex-shrink-0 px-4"
                        style={{ width: `${100 / testimonialsToShow}%` }}
                      >
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 h-full">
                          <div className="flex items-center mb-4">
                            <div className={`${testimonial.color} rounded-full w-12 h-12 flex items-center justify-center mr-4`}>
                              <span className="text-white font-bold">{testimonial.initials}</span>
                            </div>
                            <div>
                              <h4 className="font-bold text-gray-900 dark:text-white">{testimonial.name}</h4>
                              <p className="text-gray-600 dark:text-gray-400 text-sm">{testimonial.company}</p>
                            </div>
                          </div>
                          <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                            "{testimonial.text}"
                          </p>
                          <div className="flex text-yellow-400">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                            {[...Array(5 - testimonial.rating)].map((_, i) => (
                              <svg key={`empty-${i}`} className="w-5 h-5 text-gray-300 dark:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right Navigation Arrow */}
                <button
                  onClick={nextTestimonial}
                  className="flex-shrink-0 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-white p-3 rounded-full shadow-lg transition-colors z-10"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>

              {/* Dots Indicator - Show all 10 reviews, highlight based on adaptive count */}
              <div className="flex justify-center mt-8 space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(Math.min(index, maxTestimonialIndex))}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      index >= currentTestimonial && index < currentTestimonial + testimonialsToShow
                        ? 'bg-blue-600 dark:bg-blue-400'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-20 bg-white dark:bg-gray-800">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-5 lg:px-7">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                One comprehensive solution with everything you need to manage your automotive business efficiently.
              </p>
            </div>

            {/* Referral Program Banner - Commented out - keeping code for future use */}
            {/* <div className="max-w-5xl mx-auto mb-16">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 dark:from-purple-700 dark:to-pink-700 rounded-xl shadow-xl overflow-hidden">
                <div className="flex flex-col md:flex-row items-center">
                  <div className="md:w-3/4 p-8 md:p-10">
                    <div className="flex items-center mb-3">
                      <Share2 className="w-6 h-6 text-purple-100 mr-2" />
                      <h3 className="text-2xl font-bold text-white">Referral Program</h3>
                    </div>
                    <h4 className="text-xl font-bold text-white mb-3">
                      Refer 10 Friends, Get 2 Years Access FREE!
                    </h4>
                    <p className="text-purple-100 mb-4">
                      Share Torque Professional with friends and colleagues to earn rewards. For every 10 paying subscribers you refer, you'll receive 2 full years of Torque Professional access - completely free! Plus, your referrals get a free month when they sign up.
                    </p>
                    <Link
                      to="/account/referrals"
                      className="mt-2 bg-white text-purple-600 hover:bg-purple-50 px-6 py-2 rounded-md text-sm font-medium inline-flex items-center"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Get Referral Link
                    </Link>
                  </div>
                  <div className="md:w-1/4 p-6 flex items-center justify-center">
                    <div className="bg-white/20 backdrop-blur-sm p-6 rounded-full">
                      <Gift className="w-16 h-16 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div> */}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-6xl mx-auto auto-rows-fr">
              {/* Professional Plan */}
              <div className="bg-white dark:bg-gray-700 rounded-lg shadow-xl overflow-hidden border-2 border-blue-500 flex flex-col h-full">
                <div className="bg-blue-500 py-2 text-center">
                  <span className="text-white font-medium">TORQUE PROFESSIONAL</span>
                </div>
                <div className="p-6 border-b dark:border-gray-600">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Torque Professional</h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Our flagship product provides a complete solution for your automotive service business. With one simple subscription, you get access to all our professional features:
                  </p>

                  {/* Subscription Options */}
                  <div className="mb-4">
                    <div className="bg-gray-100 dark:bg-gray-600 rounded-lg p-2 flex justify-between mb-2">
                      <span className="text-sm font-medium dark:text-gray-200">Monthly</span>
                      <span className="text-sm font-bold dark:text-white">$119.99/mo</span>
                    </div>
                    <div className="bg-gray-100 dark:bg-gray-600 rounded-lg p-2 flex justify-between mb-2">
                      <span className="text-sm font-medium dark:text-gray-200">3 Months</span>
                      <span className="text-sm font-bold dark:text-white">$109.99/mo <span className="text-green-500">Save $30</span></span>
                    </div>
                    <div className="bg-gray-100 dark:bg-gray-600 rounded-lg p-2 flex justify-between mb-2">
                      <span className="text-sm font-medium dark:text-gray-200">6 Months</span>
                      <span className="text-sm font-bold dark:text-white">$99.99/mo <span className="text-green-500">Save $120</span></span>
                    </div>
                    <div className="bg-blue-100 dark:bg-blue-800 rounded-lg p-2 flex justify-between">
                      <span className="text-sm font-medium dark:text-gray-200">Annual</span>
                      <span className="text-sm font-bold dark:text-white">$999/year <span className="text-green-500">Save $440</span></span>
                    </div>
                  </div>

                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">Save up to 31%</span>
                  </div>
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    with annual subscription
                  </div>
                </div>
                <div className="p-6 flex-grow flex flex-col">
                  <ul className="space-y-4 flex-grow">
                    <li className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-300">Single User License</span>
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-300">Unlimited Invoices</span>
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-300">Advanced Customer Management</span>
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-300"><strong>Complete Vehicle Database</strong> with <strong>FREE yearly updates</strong></span>
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-300">Comprehensive Reports and Analytics</span>
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-300">Email Notifications and Reminders</span>
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-300">Easy PDF Invoice Generation</span>
                    </li>
                  </ul>
                  <Link 
                    to="/register"
                    className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-md text-sm font-medium inline-block text-center"
                  >
                    Start Free Trial
                  </Link>
                </div>
              </div>

              {/* Cloud Version - Coming Soon */}
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-700 dark:to-indigo-800 rounded-lg shadow-xl overflow-hidden flex flex-col h-full">
                <div className="bg-blue-600 dark:bg-blue-800 py-2 text-center">
                  <span className="text-white font-medium">COMING SOON</span>
                </div>
                <div className="p-6 border-b border-blue-500/30">
                  <h3 className="text-2xl font-bold text-white mb-2">Torque Cloud</h3>
                  <p className="text-blue-100 text-base mb-4">
                    Access your business data anytime, anywhere with our cloud-connected version. Collaborate with your team in real-time and enjoy automatic backups and updates.
                  </p>
                  
                  {/* Pricing Coming Soon */}
                  <div className="bg-blue-500/20 backdrop-blur-sm rounded-lg p-4 mb-4">
                    <div className="text-center">
                      <p className="text-xl font-bold text-white">Pricing will be announced soon</p>
                    </div>
                  </div>
                </div>
                <div className="p-6 text-white flex-grow flex flex-col">

                  <div className="space-y-4 mb-6 flex-grow">
                    <div className="flex items-center">
                      <div className="bg-blue-100 rounded-full p-1 mr-3 flex-shrink-0">
                        <svg className="w-4 h-4 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span>All Professional features included</span>
                    </div>
                    <div className="flex items-center ml-7">
                      <div className="bg-blue-100/50 rounded-full p-0.5 mr-3 flex-shrink-0">
                        <svg className="w-3 h-3 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-blue-100 text-sm">Unlimited Invoices</span>
                    </div>
                    <div className="flex items-center ml-7">
                      <div className="bg-blue-100/50 rounded-full p-0.5 mr-3 flex-shrink-0">
                        <svg className="w-3 h-3 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-blue-100 text-sm">Advanced Customer Management</span>
                    </div>
                    <div className="flex items-center ml-7">
                      <div className="bg-blue-100/50 rounded-full p-0.5 mr-3 flex-shrink-0">
                        <svg className="w-3 h-3 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-blue-100 text-sm">Complete Vehicle Database with FREE yearly updates</span>
                    </div>
                    <div className="flex items-center ml-7">
                      <div className="bg-blue-100/50 rounded-full p-0.5 mr-3 flex-shrink-0">
                        <svg className="w-3 h-3 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-blue-100 text-sm">Comprehensive Reports and Analytics</span>
                    </div>
                    <div className="flex items-center ml-7">
                      <div className="bg-blue-100/50 rounded-full p-0.5 mr-3 flex-shrink-0">
                        <svg className="w-3 h-3 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-blue-100 text-sm">Email Notifications and Reminders</span>
                    </div>
                    <div className="flex items-center ml-7">
                      <div className="bg-blue-100/50 rounded-full p-0.5 mr-3 flex-shrink-0">
                        <svg className="w-3 h-3 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-blue-100 text-sm">Easy PDF Invoice Generation</span>
                    </div>
                    <div className="flex items-center">
                      <div className="bg-blue-100 rounded-full p-1 mr-3 flex-shrink-0">
                        <svg className="w-4 h-4 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span>Unlimited Users</span>
                    </div>
                    <div className="flex items-center">
                      <div className="bg-blue-100 rounded-full p-1 mr-3 flex-shrink-0">
                        <svg className="w-4 h-4 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span>Cloud invoice storage and data sync</span>
                    </div>
                    <div className="flex items-center">
                      <div className="bg-blue-100 rounded-full p-1 mr-3 flex-shrink-0">
                        <svg className="w-4 h-4 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span>Real-time synchronization</span>
                    </div>
                    <div className="flex items-center">
                      <div className="bg-blue-100 rounded-full p-1 mr-3 flex-shrink-0">
                        <svg className="w-4 h-4 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span>Cross-device compatibility</span>
                    </div>
                    <div className="flex items-center">
                      <div className="bg-blue-100 rounded-full p-1 mr-3 flex-shrink-0">
                        <svg className="w-4 h-4 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span>Automatic cloud backups</span>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowNotifyModal(true)}
                    className={`mt-8 w-full py-3 rounded-md text-sm font-medium inline-flex items-center justify-center ${
                      hasNotified 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                        : 'bg-white text-indigo-600 hover:bg-indigo-50'
                    }`}
                  >
                    {hasNotified ? (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        Thank You!
                      </>
                    ) : (
                      <>
                        <Bell className="h-4 w-4 mr-2" />
                        Notify Me
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="bg-gray-100 dark:bg-gray-900 py-20">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-5 lg:px-7">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                About Us
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Learn about the team behind Torque and our mission to revolutionize automotive business management.
              </p>
            </div>

            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="md:w-1/2">
                <img
                  src="/Torque_office.png"
                  alt="Torque Office"
                  className="rounded-lg shadow-md w-full object-cover h-[28rem]"
                  style={{ objectPosition: 'center 70%' }}
                />
              </div>
              <div className="md:w-1/2">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Our Story</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Founded in 2020 by a team of automotive experts and software engineers, Torque was created to address the unique challenges faced by auto repair businesses in managing their operations efficiently.
                </p>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  We recognized that many repair shops were still using outdated methods for invoicing and customer management, leading to inefficiencies, errors, and lost revenue. Our mission is to provide a comprehensive, user-friendly solution that streamlines these processes.
                </p>
                <p className="text-gray-600 dark:text-gray-300">
                  Today, Torque is used by hundreds of automotive businesses across the country, helping them save time, reduce errors, and improve customer satisfaction.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-white dark:bg-gray-800">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-5 lg:px-7">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Get In Touch
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Have questions about Torque? Our team is here to help. Reach out to us using the form below.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <form className="space-y-6" onSubmit={handleContactSubmit}>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleContactChange}
                      className="mt-1 block w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Your name"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleContactChange}
                      className="mt-1 block w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleContactChange}
                      className="mt-1 block w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="How can we help?"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      value={formData.message}
                      onChange={handleContactChange}
                      className="mt-1 block w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Your message here..."
                      required
                    />
                  </div>
                  <div>
                    <button
                      type="submit"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-md text-sm font-medium"
                    >
                      Send Message
                    </button>
                  </div>
                </form>
              </div>

              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white text-center">Contact Information</h3>

                <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className="text-gray-900 dark:text-white font-medium mr-2">Phone:</span>
                    <span className="text-gray-600 dark:text-gray-300">(555) 123-4567</span>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className="text-gray-900 dark:text-white font-medium mr-2">Email:</span>
                    <span className="text-gray-600 dark:text-gray-300">info@torqueapp.com</span>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-700 rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Follow Us</h3>
                  <div className="flex space-x-4">
                    <a href="https://facebook.com" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </a>
                    <a href="https://twitter.com" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723 9.99 9.99 0 01-3.127 1.195 4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                      </svg>
                    </a>
                    <a href="https://youtube.com" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm4.441 16.892c-2.102.144-6.784.144-8.883 0C5.282 16.736 5.017 15.622 5 12c.017-3.629.285-4.736 2.558-4.892 2.099-.144 6.782-.144 8.883 0C18.718 7.264 18.982 8.378 19 12c-.018 3.629-.285 4.736-2.559 4.892zM10 9.658l4.917 2.338L10 14.342V9.658z" />
                      </svg>
                    </a>
                    <a href="https://linkedin.com" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm6 9.803a4.195 4.195 0 01-1.205.33 2.105 2.105 0 00.926-1.164 4.2 4.2 0 01-1.33.508 2.103 2.103 0 00-3.584 1.917 5.97 5.97 0 01-4.33-2.195 2.103 2.103 0 00.65 2.8 2.083 2.083 0 01-.95-.263v.026c0 1.018.724 1.867 1.685 2.059 2.104 2.104 0 01-.95.036 2.103 2.103 0 001.963 1.459 4.217 4.217 0 01-2.61.898 4.29 4.29 0 01-.504-.03 5.95 5.95 0 003.218.886c3.86 0 5.97-3.194 5.97-5.97 0-.09 0-.182-.005-.272a4.239 4.239 0 001.044-1.08L18 9.803z" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-blue-600 dark:bg-blue-800 py-16 text-white">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-5 lg:px-7 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Automotive Business?</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Join hundreds of auto repair shops that trust Torque to streamline their operations.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link 
                to="/register"
                className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-3 rounded-md text-lg font-medium inline-block"
              >
                Start Free Trial
              </Link>
              <Link 
                to="/pricing"
                className="bg-transparent border border-white text-white hover:bg-white hover:text-blue-600 px-6 py-3 rounded-md text-lg font-medium inline-block"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Email Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 flex items-center justify-center p-4 pt-[52px]">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-8 relative transform transition-all">
            <button
              onClick={() => setShowVerificationModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Verify Your Email Address
              </h3>

              <p className="text-gray-600 dark:text-gray-300 mb-6 text-lg">
                We've sent a verification email to your inbox. <strong>Please verify your email to receive your trial license key</strong> and start using Torque Professional.
              </p>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <strong>Note:</strong> If you don't see the email, please check your spam or junk folder.
                </p>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  <strong>Important:</strong> You won't be able to download the software until you verify your email address.
                </p>
              </div>

              <button
                onClick={() => setShowVerificationModal(false)}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg"
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notify Me Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 relative transform transition-all">
            <button
              onClick={() => setShowNotifyModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Get Notified About Torque Cloud</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Be the first to know when our cloud solution launches. Enter your email below and we'll keep you updated!
              </p>
            </div>
            
            {!hasNotified ? (
              <form onSubmit={(e) => {
                e.preventDefault();
                setHasNotified(true);
              }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowNotifyModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notify Me
                </button>
              </div>
            </form>
            ) : (
              <div className="text-center">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Thank You!</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    We've added {notifyEmail} to our notification list. You'll be the first to know when Torque Cloud launches!
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowNotifyModal(false);
                  }}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Review Invitation Popup */}
      {showReviewPopup && (
        <ReviewInvitationPopup onClose={() => setShowReviewPopup(false)} />
      )}

      <Footer />
    </div>
  );
};

export default HomePage;